import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DeleteIcon, EditSecondaryIcon } from 'assets/v2/icons';
import { SecondaryButton } from 'components/v2/Button';
import { CardContent } from 'components/v2/Card';
import { throwSuccessMsg } from 'containers/App/actions';
import useFormFieldValidator from 'utils/hooks/useFormFieldValidator';

import {
  ActionButtonWrapper,
  ButtonContainer,
  StyledActionButton,
  StyledTextField,
} from './TripDriverNote.styles';

/**
 * TripDriverNote
 *
 * A reusable component to manage driver notes for a specific trip leg.
 *
 * Supports:
 * - Viewing an existing note.
 * - Inline editing of the note with character limit enforcement (2000 chars).
 * - Saving, editing, and deleting the note.
 * - Conditional rendering of action buttons based on `isEditingAllowed`.
 *
 * This component integrates with Formik for form state and validation.
 *
 * @param {Object} props
 * @param {Object} props.formik - Formik instance containing values, setFieldValue, setFieldTouched, etc.
 * @param {number} props.legIndex - Index of the trip leg associated with this note.
 * @param {boolean} [props.isEditingAllowed=true] - Whether the note is editable. If false, updates the `instructions` field directly.
 * @param {Function} [props.handleFormChange] - Optional external handler to update form values.
 * @param {Object} [props.validationSchema] - Optional Yup schema for field validation.
 * @param {boolean} [props.isLoading=false] - Disables editing when true.
 * @param {string} [props.placeHolderText='Type your text right here...'] - Placeholder text for the note field.
 *
 * @returns {JSX.Element} A styled text field with optional action buttons for editing, saving, and deleting a driver note.
 */

const TripDriverNote = ({
  formik,
  legIndex,
  isEditingAllowed = true,
  handleFormChange,
  validationSchema,
  isLoading = false,
  placeHolderText = 'Type your text right here...',
}) => {
  const { values, setFieldValue, setFieldTouched } = formik;
  const notePath = `itineraryForm.legs[${legIndex}].notes`;
  const noteTextPath = `itineraryForm.legs.${legIndex}.notes.0.text`;

  const existingNote =
    values?.itineraryForm?.legs?.[legIndex]?.notes?.[0]?.text || '';

  const dispatch = useDispatch();

  // Local state for editing note and edit mode
  const [localNote, setLocalNote] = useState(existingNote);
  const [isEditing, setIsEditing] = useState(!existingNote);

  // Update local state if Formik values change
  useEffect(() => {
    setLocalNote(existingNote);
    setIsEditing(!existingNote);
  }, [legIndex, existingNote]);

  const { fieldValidation, fieldErrorMsg, setUpdatedField } =
    useFormFieldValidator({
      formik,
      schema: validationSchema,
    });

  // Save note to Formik state
  const saveNote = useCallback(
    async (text) => {
      const trimmed = text.trim();
      const newNotes = trimmed ? [{ noteType: 'DRIVER', text: trimmed }] : [];
      await setFieldValue(notePath, newNotes);
      setFieldTouched(noteTextPath);
      setUpdatedField(noteTextPath);
    },
    [notePath, noteTextPath, setFieldValue, setFieldTouched, setUpdatedField],
  );

  // Fallback setter for external form change
  const setValue = useCallback(
    async (key, value) => {
      await handleFormChange(key, value);
      setUpdatedField(key);
    },
    [handleFormChange, setUpdatedField],
  );

  // Handle text change for note
  const handleChange = useCallback(
    (e) => {
      if (e.target.value.length > 2000) return;
      if (isEditingAllowed) setLocalNote(e.target.value);
      else setValue('instructions', e.target.value);
    },
    [isEditingAllowed, setValue],
  );

  // Save local note to Formik
  const handleSave = useCallback(() => {
    const trimmedNote = localNote.trim();
    setLocalNote(trimmedNote);
    saveNote(trimmedNote);
    setIsEditing(false);

    dispatch(
      throwSuccessMsg(
        `Driver note has been successfully ${existingNote ? 'updated' : 'added'}`,
      ),
    );
  }, [existingNote, localNote, saveNote]);

  // Enable editing mode
  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  // Delete note and enable editing
  const handleDelete = useCallback(() => {
    setLocalNote('');
    saveNote('');
    setIsEditing(true);
    dispatch(throwSuccessMsg('Driver note has been successfully deleted'));
  }, [saveNote]);

  // Render the appropriate buttons based on edit state
  const renderButtons = useCallback(() => {
    if (isEditing) {
      return (
        <SecondaryButton
          onClick={handleSave}
          size="small"
          disabled={!localNote.trim()}
        >
          {existingNote ? 'Save Changes' : 'Save Note'}
        </SecondaryButton>
      );
    }

    return (
      <ActionButtonWrapper>
        <StyledActionButton
          color="primary"
          size="medium"
          variant="text"
          startIcon={<EditSecondaryIcon />}
          onClick={handleEdit}
        >
          Edit
        </StyledActionButton>
        <StyledActionButton
          color="primary"
          size="medium"
          variant="text"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
        >
          Delete
        </StyledActionButton>
      </ActionButtonWrapper>
    );
  }, [
    isEditing,
    localNote,
    existingNote,
    handleSave,
    handleEdit,
    handleDelete,
  ]);

  // Memoized note value depending on edit mode
  const noteValue = useMemo(
    () => (isEditingAllowed ? localNote : values.instructions),
    [isEditingAllowed, localNote, values.instructions],
  );

  return (
    <CardContent>
      <StyledTextField
        name="driverNote"
        maxLength={2000}
        value={noteValue}
        rows={6}
        onChange={handleChange}
        placeholder={placeHolderText}
        multiline
        fullWidth
        disabled={!isEditing || isLoading}
        valid={fieldValidation[noteTextPath] || fieldValidation.instructions}
        error={fieldErrorMsg[noteTextPath] || fieldErrorMsg.instructions}
      />
      {/* Render the edit/delete/save buttons only if editing is allowed */}
      {isEditingAllowed && <ButtonContainer>{renderButtons()}</ButtonContainer>}
    </CardContent>
  );
};

TripDriverNote.propTypes = {
  formik: PropTypes.object.isRequired,
  legIndex: PropTypes.number.isRequired,
  isEditingAllowed: PropTypes.bool,
  handleFormChange: PropTypes.func,
  validationSchema: PropTypes.object,
  isLoading: PropTypes.bool,
  placeHolderText: PropTypes.string,
};

export default memo(TripDriverNote);
